import type { FormasPlural } from './catalogo';
import { en } from './catalogos/en';
import { es } from './catalogos/es';

/** Los idiomas que maneja el producto. Agregar uno es agregarlo acá y escribir su catálogo. */
export const IDIOMAS = ['es', 'en'] as const;
export type Idioma = (typeof IDIOMAS)[number];

/** Toda clave que existe. Sale del catálogo español, que es la fuente de verdad. */
export type Clave = keyof typeof es;

/** Lo que se le pasa a un texto: `Hola {nombre}` necesita `{ nombre: 'Ana' }`. */
export type Valores = Record<string, string | number>;

const CATALOGOS: Record<Idioma, Record<string, string | FormasPlural>> = { es, en };

/**
 * Las reglas de plural de cada idioma, creadas una sola vez.
 *
 * `Intl.PluralRules` no es gratis de construir y se llamaría en cada dibujado de
 * cada componente. Guardarlas acá es la diferencia entre un detalle y un
 * problema de rendimiento en una lista larga — que es justo lo que va a tener la
 * PWA del mecánico.
 */
const REGLAS = new Map<Idioma, Intl.PluralRules>();
function reglasDe(idioma: Idioma): Intl.PluralRules {
  let reglas = REGLAS.get(idioma);
  if (!reglas) {
    reglas = new Intl.PluralRules(idioma);
    REGLAS.set(idioma, reglas);
  }
  return reglas;
}

/**
 * Reemplaza `{nombre}` por su valor.
 *
 * Si falta un valor, **se deja el hueco a la vista** en vez de poner "undefined"
 * o una cadena vacía. Un `Hola {nombre}` en pantalla se reporta; un `Hola ` pasa
 * desapercibido durante meses.
 */
function rellenar(plantilla: string, valores: Valores | undefined): string {
  if (!valores) return plantilla;
  return plantilla.replace(/\{(\w+)\}/g, (hueco, nombre: string) => {
    const valor = valores[nombre];
    return valor === undefined ? hueco : String(valor);
  });
}

/**
 * Devuelve la función que traduce, ya atada a un idioma.
 *
 * Se usa a través de `useTraduccion()` en un componente. Esta función suelta
 * existe para lo que no es React: una prueba, o un mensaje que se arma fuera de
 * la pantalla.
 */
export function crearTraductor(idioma: Idioma) {
  const catalogo = CATALOGOS[idioma];

  return function t(clave: Clave, valores?: Valores): string {
    const mensaje = catalogo[clave];

    // Los tipos ya impiden llegar acá con una clave inexistente. Esto cubre lo
    // que los tipos no ven: una clave armada en tiempo de ejecución, o un
    // catálogo que quedó a medias.
    if (mensaje === undefined) {
      if (import.meta.env?.DEV) {
        throw new Error(`Falta la traducción "${clave}" en el catálogo "${idioma}".`);
      }
      // En producción no se rompe la pantalla por un texto: se muestra la clave,
      // que es feo, evidente y reportable.
      return clave;
    }

    if (typeof mensaje === 'string') {
      return rellenar(mensaje, valores);
    }

    // Texto con plural: hace falta el número, y va en `n` por convención.
    const n = valores?.['n'];
    if (typeof n !== 'number') {
      if (import.meta.env?.DEV) {
        throw new Error(`La traducción "${clave}" lleva plural y necesita un valor numérico en "n".`);
      }
      return clave;
    }

    const forma = reglasDe(idioma).select(n);
    // `other` siempre existe en todos los idiomas: es el respaldo natural si un
    // catálogo no declaró la forma que tocaba.
    const plantilla = mensaje[forma] ?? mensaje.other;
    if (plantilla === undefined) {
      return clave;
    }

    return rellenar(plantilla, valores);
  };
}

export type Traductor = ReturnType<typeof crearTraductor>;
